import api from './api';

/**
 * Service API pour le module de regulation
 * SFD v2.1: Timeline, conflits, optimisation
 */

// Types pour la regulation
export interface TimelineJalon {
  type: string;
  type_label: string;
  heure_prevue: string | null;
  heure_reelle: string | null;
  ecart_minutes: number | null;
  color: string;
}

export interface TimelineEquipeAffectation {
  affectation_id: number;
  intervention_id: number;
  numero_ordre: string;
  parking: string;
  heure_souhaitee: string;
  heure_planifiee: string;
  heure_debut: string; // Retrocompatibilite
  // Date+heure ISO complete (timeline continue multi-jours, #timeline-range) -
  // 'heure_planifiee' seule (H:i) ne permet pas de calculer une position
  // absolue sur plusieurs jours.
  heure_planifiee_iso: string | null;
  zone: number;
  statut: string;
}

export interface TimelineEquipe {
  id: number;
  nom: string;
  type_engin: string;
  type_engin_label: string;
  /** Nom du chef, seulement s'il est au planning a la date de cette timeline (null sinon). */
  chef: string | null;
  /** Vehicule affecte a l'equipe (meme referentiel que l'ecran Equipes), ou null. */
  engin: { id: number; nom: string; immatriculation: string | null; disponible: boolean } | null;
  affectations: TimelineEquipeAffectation[];
  charge: number;
}

export interface TimelineIntervention {
  id: number;
  numero_ordre: string;
  parking: string;
  heure_souhaitee: string;
  heure_planifiee: string;
  statut: string;
  statut_label: string;
  priorite: string;
  priorite_label: string;
  progression: number;
  equipes: Array<{
    affectation_id: number | null;
    id: number | null;
    nom: string | null;
    zone: number;
    heure_planifiee: string | null;
  }>;
  jalons: TimelineJalon[];
  en_retard: boolean;
  // SFD jalonnement Contact/Large (Lot 5 - #125) : null pour les
  // interventions anterieures (`configuration` null cote backend).
  jalons_termines: number | null;
  jalons_attendus: number | null;
}

export interface TimelineStats {
  total: number;
  en_attente: number;
  affectees: number;
  en_cours: number;
  terminees: number;
  en_retard: number;
  ecarts?: {
    total: number;
    en_retard: number;
    en_avance: number;
    dans_les_temps: number;
    ecart_moyen: number;
  };
}

export interface TimelineResponse {
  date: string;
  interventions: TimelineIntervention[];
  equipes: TimelineEquipe[];
  plages_horaires: string[];
  statistiques: TimelineStats;
}

/**
 * Timeline continue multi-jours (page regulation pleine largeur) : une seule
 * frise scrollable sur plusieurs jours consecutifs, pour les operations qui
 * s'etendent sur la nuit (demande utilisateur).
 */
export interface TimelineRangeResponse {
  date_debut: string;
  nb_jours: number;
  jours: string[];
  interventions: TimelineIntervention[];
  equipes: TimelineEquipe[];
}

export interface Conflit {
  type: 'surcharge' | 'incompatibilite' | 'non_affecte' | 'retard';
  niveau: 'critique' | 'warning' | 'info';
  message: string;
  intervention_id?: number;
  equipe_id?: number;
  interventions?: number[];
  zones?: number[];
  zone?: number;
  ecart_minutes?: number;
  minutes_restantes?: number;
}

export interface ConflitsResponse {
  conflits: Conflit[];
  total: number;
  par_niveau: {
    critique: number;
    warning: number;
    info: number;
  };
}

export interface Suggestion {
  type: 'affectation' | 'reequilibrage';
  intervention_id?: number;
  intervention_numero?: string;
  equipe_id?: number;
  equipe_nom?: string;
  zone?: number;
  raison: string;
  equipe_source_id?: number;
  equipe_source_nom?: string;
  equipe_cible_id?: number;
  equipe_cible_nom?: string;
}

export interface OptimizationResponse {
  suggestions: Suggestion[];
  total: number;
  gain_estime: number;
}

export interface ReassignDTO {
  intervention_id: number;
  zone: number;
  equipe_id: number;
}

export interface ReassignResponse {
  message: string;
  affectation: {
    id: number;
    intervention_id: number;
    equipe_id: number;
    equipe_nom: string;
    zone: number;
    statut: string;
  };
}

export interface ApplySuggestionsDTO {
  date: string;
  suggestions?: number[];
}

export interface ApplySuggestionsResponse {
  applied: Array<{
    intervention: string;
    equipe: string;
    zone: number;
  }>;
  total_applied: number;
  errors: Array<{
    suggestion: number;
    error: string;
  }>;
}

export interface StatsResponse {
  periode: {
    debut: string;
    fin: string;
  };
  total_interventions: number;
  par_statut: Record<string, number>;
}

// Types pour l'optimisation avancee
export interface OptimizationStats {
  total_affectations: number;
  interventions_traitees: number;
  interventions_totales: number;
  charge_par_equipe: Record<string, number>;
  charge_max: number;
  charge_min: number;
  charge_moyenne: number;
  ecart_charge: number;
  par_priorite: {
    critique: number;
    urgente: number;
    normale: number;
  };
}

export interface PlanAffectation {
  intervention_id: number;
  intervention_numero: string;
  equipe_id: number;
  equipe_nom: string;
  zone: number;
  parking?: string;
  heure?: string;
  priorite?: string;
}

export interface NonAffectable {
  intervention_id: number;
  intervention_numero: string;
  zone: number;
  raison: string;
}

export interface GeneratePlanResponse {
  date: string;
  plan: PlanAffectation[];
  score_global: number;
  statistiques: OptimizationStats;
  non_affectables: NonAffectable[];
  resume: {
    total_affectations: number;
    interventions_traitees: number;
    interventions_totales: number;
    taux_couverture: number;
  };
}

export interface ApplyPlanDTO {
  affectations: PlanAffectation[];
}

export interface ApplyPlanResponse {
  message: string;
  applied: number;
  errors: Array<{
    affectation: PlanAffectation;
    error: string;
  }>;
}

export interface CalculateScoreDTO {
  intervention_id: number;
  equipe_id: number;
  zone: number;
}

export interface UpdateScheduleDTO {
  affectation_id?: number;
  intervention_id?: number;
  heure_planifiee: string;
}

export interface UpdateScheduleResponse {
  success: boolean;
  message: string;
  intervention: {
    id: number;
    numero_ordre: string;
    heure_souhaitee: string;
    heure_planifiee: string;
  };
  affectations_modifiees: number;
}

export interface CalculateScoreResponse {
  compatible: boolean;
  score: number;
  score_max: number;
  qualite?: string;
  message?: string;
  details?: {
    intervention: string;
    equipe: string;
    zone: number;
    priorite: string;
    charge_equipe: number;
  };
}

export const regulationService = {
  /**
   * Recupere la timeline de regulation pour une date
   */
  async getTimeline(date: string): Promise<TimelineResponse> {
    const response = await api.get<TimelineResponse>('/regulation/timeline', {
      params: { date },
    });
    return response.data;
  },

  /**
   * Timeline continue multi-jours (page pleine largeur)
   */
  async getTimelineRange(dateDebut: string, nbJours: number): Promise<TimelineRangeResponse> {
    const response = await api.get<TimelineRangeResponse>('/regulation/timeline-range', {
      params: { date: dateDebut, jours: nbJours },
    });
    return response.data;
  },

  /**
   * Detecte les conflits d'affectation pour une date
   */
  async getConflicts(date: string): Promise<ConflitsResponse> {
    const response = await api.get<ConflitsResponse>('/regulation/conflicts', {
      params: { date },
    });
    return response.data;
  },

  /**
   * Suggere une optimisation des affectations
   */
  async getOptimization(date: string): Promise<OptimizationResponse> {
    const response = await api.post<OptimizationResponse>('/regulation/optimize', {
      date,
    });
    return response.data;
  },

  /**
   * Reassigne une zone a une autre equipe
   */
  async reassign(data: ReassignDTO): Promise<ReassignResponse> {
    const response = await api.patch<ReassignResponse>('/regulation/reassign', data);
    return response.data;
  },

  /**
   * Applique automatiquement les suggestions d'optimisation
   */
  async applySuggestions(data: ApplySuggestionsDTO): Promise<ApplySuggestionsResponse> {
    const response = await api.post<ApplySuggestionsResponse>(
      '/regulation/apply-suggestions',
      data
    );
    return response.data;
  },

  /**
   * Recupere les statistiques de regulation pour une periode
   */
  async getStats(dateDebut: string, dateFin: string): Promise<StatsResponse> {
    const response = await api.get<StatsResponse>('/regulation/stats', {
      params: { date_debut: dateDebut, date_fin: dateFin },
    });
    return response.data;
  },

  /**
   * Genere un plan d'affectation optimise complet
   * Utilise l'algorithme avance d'optimisation
   */
  async generatePlan(date: string): Promise<GeneratePlanResponse> {
    const response = await api.post<GeneratePlanResponse>('/regulation/generate-plan', {
      date,
    });
    return response.data;
  },

  /**
   * Applique un plan d'affectation genere
   */
  async applyPlan(data: ApplyPlanDTO): Promise<ApplyPlanResponse> {
    const response = await api.post<ApplyPlanResponse>('/regulation/apply-plan', data);
    return response.data;
  },

  /**
   * Calcule le score d'une affectation potentielle
   */
  async calculateScore(data: CalculateScoreDTO): Promise<CalculateScoreResponse> {
    const response = await api.post<CalculateScoreResponse>('/regulation/calculate-score', data);
    return response.data;
  },

  /**
   * Modifie l'heure de debut d'une intervention (drag & drop sur timeline)
   */
  async updateSchedule(data: UpdateScheduleDTO): Promise<UpdateScheduleResponse> {
    const response = await api.patch<UpdateScheduleResponse>('/regulation/update-schedule', data);
    return response.data;
  },
};

export default regulationService;
